/**
 * TECH-PREF-CONFIRM (34i82, Seth 2026-10-20: "Proceed") — Settings › Tech Requests.
 *
 * Every "Brent only" / "not Miles" / VIP note the extractor found in twenty
 * years of ESC notes, waiting for a human answer. Confirm → the 360 badge goes
 * solid, Book a Call pre-picks the tech, and the extractor never rewrites it.
 * Not right → gone for good (extract-notes v16 never re-proposes an answered
 * fact, in any wording). Grouped by WHO, because that is how the office reads
 * it ("Chet retired — all of his can go"): a tech no longer on the roster is
 * flagged, and a whole group can be cleared or confirmed in one move.
 *
 * Competitors: ServiceTitan keeps preferred and excluded technicians as typed
 * fields on the location record; FieldEdge has a preferred-tech field on the
 * customer; Jobber and Housecall Pro leave it to tags and notes. Pegasus mines
 * them out of the notes already written and asks for one tap, not 500 typings.
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Search, Star, UserRound, UserX, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { CustomerLink } from "@/components/links";
import { cleanPartyName } from "@/features/customer-360/houseHistory";
import { stripStars } from "@/features/customer-360/watchouts";
import { useCan } from "@/lib/permissions/PermissionProvider";

interface ReviewRow {
  id: string;
  custno: string;
  locno: string | null;
  fullname: string | null;
  add1: string | null;
  city: string | null;
  signal_type: "tech_pref" | "vip";
  value: string;
  detail: Record<string, unknown> | null;
  source_text: string | null;
  confidence: number | null;
  created_at: string | null;
  last_service: string | null;
  customer_inactive: boolean;
}
interface Employee { empno: string; empname: string | null; firstname: string | null; lastname: string | null; dept: string | null; inactive: boolean | null }

function useSignalReviewList() {
  return useQuery({
    queryKey: ["signal_review", "tech_pref+vip"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<ReviewRow[]> => {
      const { data, error } = await supabase.rpc("signal_review_list", { p_types: ["tech_pref", "vip"], p_limit: 3000 });
      if (error) throw error;
      return (data ?? []) as ReviewRow[];
    },
  });
}
/** Everyone ESC has ever had on payroll — the inactive flag is the point here. */
function useEmployees() {
  return useQuery({
    queryKey: ["employees_all_for_review"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<Employee[]> => {
      const { data, error } = await supabase.from("mirror_employee").select("empno, empname, firstname, lastname, dept, inactive").limit(2000);
      if (error) throw error;
      return ((data ?? []) as Employee[]).filter((e) => !["0000", "0001", "7777", "8888", "9999"].includes(e.empno));
    },
  });
}
function useResolveMany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { ids: string[]; status: "confirmed" | "dismissed" }) => {
      if (v.ids.length === 0) return;
      const { data: u } = await supabase.auth.getUser();
      const now = new Date().toISOString();
      // 200 at a time keeps the URL filter short.
      for (let i = 0; i < v.ids.length; i += 200) {
        const { error } = await supabase
          .from("extracted_signal")
          .update({
            status: v.status,
            confirmed_by: v.status === "confirmed" ? (u?.user?.email ?? null) : null,
            confirmed_at: v.status === "confirmed" ? now : null,
            updated_at: now,
          })
          .in("id", v.ids.slice(i, i + 200));
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["signal_review"] });
      qc.invalidateQueries({ queryKey: ["customer_signals"] });
    },
  });
}

const EXCLUDE_RE = /^(not|no|avoid|don'?t( send)?|do not( send)?)\s+/i;
/** "Not Miles" → { who: "Miles", exclude: true }; "Matt B only" → { who: "Matt B" }. */
function parseWho(value: string, detail: Record<string, unknown> | null): { who: string; exclude: boolean } {
  const v = stripStars(value).replace(/\*/g, "").trim();
  const exclude = !!(detail && (detail as { exclude?: boolean }).exclude) || EXCLUDE_RE.test(v);
  // "Kevin, Brent, Ron only" / "Brent or Wayne" → the first name named; the rest ride along in the row's value.
  const who = v.replace(EXCLUDE_RE, "").replace(/\s+(only|preferred|please|requested)$/i, "").split(/\s*,\s*|\s+(?:or|and|\/)\s+/i)[0].trim();
  return { who, exclude };
}
/** The roster people a typed first name (+ optional surname initial) could mean. */
function matchRoster(who: string, roster: Employee[]): Employee[] {
  const parts = who.toLowerCase().split(/\s+/).filter(Boolean);
  const first = parts[0] ?? "";
  if (first.length < 2) return [];
  const initial = parts[1]?.replace(/\./g, "") ?? "";
  const byFirst = roster.filter((e) => {
    const f = (e.firstname ?? "").toLowerCase();
    return f && (f === first || f.startsWith(first) || first.startsWith(f) && f.length >= 3);
  });
  if (!initial) return byFirst;
  const byInitial = byFirst.filter((e) => (e.lastname ?? "").toLowerCase().startsWith(initial));
  return byInitial.length ? byInitial : byFirst;
}
type RosterState = "active" | "gone" | "unknown";

interface Group {
  key: string;
  title: string;
  sub: string | null;
  state: RosterState;
  exclude: boolean;
  kind: "tech" | "vip";
  rows: ReviewRow[];
}

const monthYear = (d: string | null) => {
  if (!d) return null;
  const dt = new Date(d + "T00:00:00");
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

export function TechRequestsSection() {
  const canCurate = useCan("customers.edit");
  const { data: rows, isLoading } = useSignalReviewList();
  const { data: roster } = useEmployees();
  const resolve = useResolveMany();
  const [q, setQ] = useState("");
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
  const [arming, setArming] = useState<string | null>(null); // "<groupKey>|confirmed" while the bulk button waits for its second click

  const groups = useMemo<Group[]>(() => {
    const out = new Map<string, Group>();
    const dept03 = (roster ?? []).filter((e) => e.dept === "03");
    for (const r of rows ?? []) {
      if (r.signal_type === "vip") {
        const g = out.get("vip") ?? { key: "vip", title: "VIP", sub: "Friends, family, vendors — people the notes mark as special", state: "active", exclude: false, kind: "vip", rows: [] };
        g.rows.push(r);
        out.set("vip", g);
        continue;
      }
      const { who, exclude } = parseWho(r.value, r.detail);
      if (!who || /do not service|\bdns\b/i.test(who)) {
        const g = out.get("misfiled") ?? { key: "misfiled", title: "Not a tech name", sub: "Filed as a tech request by mistake — clear these", state: "unknown", exclude: false, kind: "tech", rows: [] };
        g.rows.push(r); out.set("misfiled", g); continue;
      }
      const matches = matchRoster(who, dept03.length ? dept03 : (roster ?? []));
      const exact = matches.length === 1 ? matches[0] : matches.find((m) => (m.firstname ?? "").toLowerCase() === who.toLowerCase().split(/\s+/)[0]) ?? null;
      const person = matches.length === 1 ? matches[0] : (matches.length > 1 && exact && matches.filter((m) => !m.inactive).length === 1 ? matches.find((m) => !m.inactive)! : null);
      const state: RosterState = person ? (person.inactive ? "gone" : "active") : matches.length > 0 ? (matches.every((m) => m.inactive) ? "gone" : "unknown") : "unknown";
      const name = person ? (person.empname || `${person.firstname ?? ""} ${person.lastname ?? ""}`.trim()) : who;
      const key = `${exclude ? "-" : "+"}${(person?.empno ?? who.toLowerCase())}`;
      const g = out.get(key) ?? {
        key,
        title: exclude ? `Don't send ${name}` : name,
        sub: person
          ? (person.inactive ? "No longer on the roster" : null)
          : matches.length > 1 ? `Could be ${matches.map((m) => m.empname ?? m.lastname).join(" or ")}` : "Not on the roster",
        state, exclude, kind: "tech", rows: [],
      };
      g.rows.push(r);
      out.set(key, g);
    }
    const list = [...out.values()];
    for (const g of list) g.rows.sort((a, b) => (b.last_service ?? "").localeCompare(a.last_service ?? ""));
    // Active techs first (biggest first), then the departed, then the unknowns, VIP last.
    const rank = (g: Group) => (g.kind === "vip" ? 3 : g.key === "misfiled" ? 2.5 : g.state === "active" ? 0 : g.state === "gone" ? 1 : 2);
    return list.sort((a, b) => rank(a) - rank(b) || b.rows.length - a.rows.length);
  }, [rows, roster]);

  const needle = q.trim().toLowerCase();
  const shown = needle
    ? groups.map((g) => ({ ...g, rows: g.rows.filter((r) => `${g.title} ${r.value} ${r.fullname ?? ""} ${r.custno} ${r.city ?? ""} ${r.source_text ?? ""}`.toLowerCase().includes(needle)) })).filter((g) => g.rows.length > 0)
    : groups;
  const total = (rows ?? []).length;
  const toggle = (k: string) => setOpenKeys((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const bulk = (g: Group, status: "confirmed" | "dismissed") => {
    const armKey = `${g.key}|${status}`;
    if (arming !== armKey) { setArming(armKey); window.setTimeout(() => setArming((a) => (a === armKey ? null : a)), 6000); return; }
    setArming(null);
    resolve.mutate({ ids: g.rows.map((r) => r.id), status });
  };

  return (
    <div className="rounded-xl border border-hairline bg-white p-5 shadow-e1">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-900">Tech Requests</h3>
        <span className="text-xs text-slate-400">{isLoading ? "counting…" : `${total} to answer`}</span>
        <InfoTip label="What this is">
          Twenty years of notes say things like &ldquo;Brent only&rdquo;, &ldquo;do not send Miles&rdquo; or &ldquo;friend of the owner&rdquo;. Pegasus already pulled those out as proposals: the 360 shows them as a dashed badge and Book a Call steers by them, but nothing is confirmed until a person says so. Confirm → the badge goes solid and the request is pinned (the notes extractor never rewrites it). Not right → it is gone for good. A tech who has left the company is flagged so the whole group can be cleared at once.
        </InfoTip>
        <label className="ml-auto flex items-center gap-1.5 rounded-md border border-hairline px-2 py-1 text-xs text-slate-500 focus-within:border-brand-300">
          <Search className="h-3.5 w-3.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tech, customer, number…" className="w-44 bg-transparent text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none" />
          {q && <button type="button" onClick={() => setQ("")} className="text-slate-400 hover:text-slate-600"><X className="h-3 w-3" /></button>}
        </label>
      </div>

      {isLoading ? (
        <p className="mt-3 text-sm text-slate-400">Reading the notes…</p>
      ) : shown.length === 0 ? (
        <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-slate-500"><Check className="h-4 w-4 text-emerald-600" /> {needle ? "Nothing matches." : "Nothing waiting — every request has been answered."}</p>
      ) : (
        <div className="mt-3 space-y-2">
          {shown.map((g) => {
            const open = openKeys.has(g.key) || !!needle;
            const Icon = g.kind === "vip" ? Star : g.exclude ? UserX : UserRound;
            const armedC = arming === `${g.key}|confirmed`;
            const armedD = arming === `${g.key}|dismissed`;
            return (
              <div key={g.key} className={cn("rounded-lg border", g.state === "gone" ? "border-amber-200/80 bg-amber-50/30" : g.state === "unknown" && g.kind === "tech" ? "border-dashed border-slate-300" : "border-hairline")}>
                <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <button type="button" onClick={() => toggle(g.key)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                    <ChevronDown className={cn("h-4 w-4 shrink-0 text-slate-400 transition-transform", open && "rotate-180")} />
                    <span className={cn("grid h-6 w-6 shrink-0 place-items-center rounded-full", g.kind === "vip" ? "bg-amber-50 text-amber-700" : g.exclude ? "bg-rose-50 text-rose-600" : "bg-violet-50 text-violet-700")}>
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-slate-900">{g.title} <span className="font-normal text-slate-400">· {g.rows.length}</span></span>
                      {g.sub && <span className={cn("block truncate text-[11px]", g.state === "gone" ? "font-semibold text-amber-700" : "text-slate-400")}>{g.sub}</span>}
                    </span>
                  </button>
                  {canCurate && g.rows.length > 1 && (
                    <span className="flex shrink-0 items-center gap-1.5">
                      {g.state !== "gone" && g.key !== "misfiled" && (
                        <Button size="sm" variant={armedC ? "primary" : "secondary"} disabled={resolve.isPending} onClick={() => bulk(g, "confirmed")} title="Confirm every request in this group">
                          {armedC ? `Confirm all ${g.rows.length}?` : "Confirm All"}
                        </Button>
                      )}
                      <Button size="sm" variant={armedD ? "danger" : "secondary"} disabled={resolve.isPending} onClick={() => bulk(g, "dismissed")} title="Clear every request in this group — they will not come back">
                        {armedD ? `Clear all ${g.rows.length}?` : "Clear All"}
                      </Button>
                    </span>
                  )}
                </div>
                {open && (
                  <ul className="divide-y divide-hairline/70 border-t border-hairline/70">
                    {g.rows.slice(0, 80).map((r) => (
                      <ReviewLine key={r.id} r={r} canCurate={canCurate} busy={resolve.isPending} onAnswer={(status) => resolve.mutate({ ids: [r.id], status })} />
                    ))}
                    {g.rows.length > 80 && <li className="px-3 py-2 text-[11px] text-slate-400">+{g.rows.length - 80} more — narrow with the search box</li>}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReviewLine({ r, canCurate, busy, onAnswer }: { r: ReviewRow; canCurate: boolean; busy: boolean; onAnswer: (s: "confirmed" | "dismissed") => void }) {
  const name = cleanPartyName(r.fullname, null) || `#${r.custno}`;
  const snippet = stripStars(r.source_text ?? "");
  const visit = monthYear(r.last_service);
  const stale = !r.last_service || r.last_service < new Date(Date.now() - 5 * 365 * 86400000).toISOString().slice(0, 10);
  return (
    <li className="flex items-start gap-3 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <CustomerLink custno={r.custno} locno={r.locno} className="text-[13px] font-semibold">{name}</CustomerLink>
          <span className="font-mono text-[11px] text-slate-400">{r.custno}</span>
          {(r.add1 || r.city) && <span className="truncate text-[11.5px] text-slate-500">{[r.add1, r.city].filter(Boolean).join(", ")}</span>}
          <span className={cn("text-[11px]", stale ? "text-slate-400" : "text-slate-500")} title="Last visit on record">{visit ? `last visit ${visit}` : "no visits on record"}</span>
          {r.customer_inactive && <span className="rounded bg-slate-100 px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-slate-500">inactive</span>}
        </div>
        <div className="mt-0.5 text-[12.5px] text-slate-700">
          <span className="font-semibold">{stripStars(r.value)}</span>
          {snippet && snippet.toLowerCase() !== stripStars(r.value).toLowerCase() && <span className="text-slate-500"> — <i>“{snippet.slice(0, 160)}{snippet.length > 160 ? "…" : ""}”</i></span>}
        </div>
      </div>
      {canCurate && (
        <span className="flex shrink-0 items-center gap-1 pt-0.5">
          <button type="button" disabled={busy} onClick={() => onAnswer("confirmed")} title="That's right — confirm it" className="rounded-md border border-hairline p-1.5 text-emerald-600 transition-colors hover:border-emerald-300 hover:bg-emerald-50 disabled:opacity-50"><Check className="h-3.5 w-3.5" /></button>
          <button type="button" disabled={busy} onClick={() => onAnswer("dismissed")} title="Not right — clear it" className="rounded-md border border-hairline p-1.5 text-slate-400 transition-colors hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"><X className="h-3.5 w-3.5" /></button>
        </span>
      )}
    </li>
  );
}
